import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { friendlyAuthError } from '../utils/authErrors';
import '../styles/auth.css';

export default function Login() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Set by the verify page (verified) and forgot-password page (message)
  const navState = location.state as { verified?: boolean; message?: string } | null;
  const successMessage = navState?.verified === true
    ? 'Your email is verified — sign in to get started.'
    : navState?.message || '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      navigate('/dashboard');
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Login failed';
      if (message.includes('not confirmed')) {
        navigate('/verify', { state: { email } });
        return;
      }
      setError(friendlyAuthError(err, 'Login failed — please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-title">Welcome back</h1>
          <p className="auth-subtitle">Sign in to your top<span style={{ color: 'var(--color-primary)' }}>zite</span> account</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {successMessage && !error && (
            <div className="auth-success" role="status">
              {successMessage}
            </div>
          )}
          {error && <div className="auth-error">{error}</div>}

          <div className="form-field">
            <label className="field-label">Email</label>
            <input
              type="email"
              className="field-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoFocus
            />
          </div>

          <div className="form-field">
            <label className="field-label">Password</label>
            <input
              type="password"
              className="field-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="auth-footer">
          <Link to="/forgot-password" className="auth-link">
            Forgot password?
          </Link>
        </p>

        <p className="auth-footer">
          Don't have an account?{' '}
          <Link to="/signup" className="auth-link">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
